import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Input,
  message,
  Popconfirm,
  Radio,
  Table,
  TableColumnsType,
  TableProps,
} from "antd";
import { IconActionKey, IconArrowDown, IconDownload2 } from "assets/svg";
import {
  INQUIRY_FILTER_STATUS_TYPE,
  INQUIRY_FILTER_SORT,
  INQUIRY_FILTER_USER_TYPE,
} from "common/constants";
import { Inquiry, InquiryFilter } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import { Link, useLocation, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { parseErrorMessage } from "common/utils";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import {
  useDeleteInquiryMutation,
  useDownloadInquiryCsvMutation,
  useGetInquiriesQuery,
} from "reducers/inquiryApi";
import InquiryItem from "components/InquiryItem";
import { UTCToJapanTime } from "common/helper/time.helper";
import { usePagination } from "common/hooks/usePagination";
import { PAGINATION } from "common/enums/Pagination.enum";
import { useCommonContext } from "contexts/CommonContext";
import { companyInfoStatusMap } from "common/helper/text.helper";
import { ConversationInquiryTypeText } from "common/enums/ConversationInquiryType.enum";

const columns: TableColumnsType<Inquiry> = [
  {
    title: "お問い合わせID",
    dataIndex: "code",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <Link
        to={`/admin/conversation-inquiry/${record.id}`}
        className="text-primary"
      >
        #{value}
      </Link>
    ),
  },
  {
    title: "件名",
    dataIndex: "subject",
    align: "center",
  },
  {
    title: "種別",
    dataIndex: "type",
    align: "center",
    render: (value) => ConversationInquiryTypeText(value),
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value) => companyInfoStatusMap[value],
  },
  {
    title: "最終更新",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "操作",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <>
        <Link
          to={`/admin/conversation-inquiry/${record.id}`}
          className="text-primary"
        >
          詳細画面へ
        </Link>
      </>
    ),
  },
];

const CompanyRequestsPage = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const companyCode = location.state?.companyCode;
  const { setHeaderTitle } = useCommonContext();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRowKeyMobile, setSelectedRowKeyMobile] = useState<string[]>(
    [],
  );
  const [searchValueDebounced, setSearchValueDebounced] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    pagination,
    paginationMobile,
    onChangePage,
    reset: resetPagination,
    resetMobile: resetPaginationMobile,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const defaultValues: InquiryFilter = {
    search: "",
    type: [
      INQUIRY_FILTER_USER_TYPE.ABOUT_LIST.value,
      INQUIRY_FILTER_USER_TYPE.ABOUT_TRANSACTION.value,
      INQUIRY_FILTER_USER_TYPE.ABOUT_ISSUE.value,
      INQUIRY_FILTER_USER_TYPE.OTHER.value,
    ],
    status: [
      INQUIRY_FILTER_STATUS_TYPE.UNRESOLVED.value,
      INQUIRY_FILTER_STATUS_TYPE.PROCESSING.value,
      INQUIRY_FILTER_STATUS_TYPE.INQUIRY_CLOSED.value,
    ],
    sort: INQUIRY_FILTER_SORT.NEWEST.value,
    company_id: companyId,
  };

  const { handleSubmit, control, watch, reset } = useForm<InquiryFilter>({
    defaultValues,
  });

  const inquiryList: InquiryFilter = watch();
  const prevInquiryList = useRef<InquiryFilter>(defaultValues);

  const {
    data: inquiryData,
    error: inquiryErr,
    refetch: refetchInquiry,
  } = useGetInquiriesQuery({
    filter: {
      ...inquiryList,
      search: searchValueDebounced,
    },
    page: pagination.current,
  });

  const {
    data: inquiryMobileData,
    error: inquiryMobileErr,
    refetch: refetchInquiryMobile,
  } = useGetInquiriesQuery({
    filter: {
      ...inquiryList,
      search: searchValueDebounced,
    },
    page: paginationMobile.current,
  });

  const [deleteInquiry] = useDeleteInquiryMutation();
  const [downloadInquiryCsv] = useDownloadInquiryCsvMutation();

  const plainTypeOptions = Object.values(INQUIRY_FILTER_USER_TYPE).map(
    (o) => o.value,
  );
  const plainStatusOptions = Object.values(INQUIRY_FILTER_STATUS_TYPE).map(
    (o) => o.value,
  );

  const [checkedTypeList, setCheckedTypeList] =
    useState<string[]>(plainTypeOptions);
  const [checkedStatusList, setCheckedStatusList] =
    useState<string[]>(plainStatusOptions);

  const checkAllType = plainTypeOptions.length === checkedTypeList.length;
  const indeterminateType =
    checkedTypeList.length > 0 &&
    checkedTypeList.length < plainTypeOptions.length;

  const checkAllStatus = plainStatusOptions.length === checkedStatusList.length;
  const indeterminateStatus =
    checkedStatusList.length > 0 &&
    checkedStatusList.length < plainStatusOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const rowSelection: TableProps<Inquiry>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ name: record.code }),
    hideSelectAll: true,
  };

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allKeys = inquiryData?.data?.items?.map((item) => item.code) ?? [];
      setSelectedRowKeys(allKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleCheckAllMobile = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeyMobile(
        inquiryMobileData?.data?.items.map((item) => item.code) || [],
      );
    } else {
      setSelectedRowKeyMobile([]);
    }
  };

  const downloadCsv = async () => {
    await downloadInquiryCsv({
      filter: inquiryList,
      fileName: "お問い合わせ管理.csv",
    });
  };

  const confirm = async () => {
    try {
      const keys = !isEmpty(selectedRowKeys)
        ? selectedRowKeys
        : !isEmpty(selectedRowKeyMobile)
          ? selectedRowKeyMobile
          : [];
      if (!isEmpty(keys)) {
        await deleteInquiry(keys).unwrap();
        message.success("削除に成功しました");
        resetPagination();
        resetPaginationMobile();
      }
    } catch (err) {
      message.error(parseErrorMessage(err));
    }
  };

  const onReset = async () => {
    resetPagination();
    resetPaginationMobile();
    reset();
    setSearchValueDebounced("");
    setCheckedStatusList(plainStatusOptions);
    setCheckedTypeList(plainTypeOptions);
  };

  const onSubmit = async () => {
    resetPagination();
    resetPaginationMobile();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchValueDebounced(inquiryList.search);
    setTimeout(() => {
      refetchInquiry();
      refetchInquiryMobile();
    }, 0);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchValueDebounced(inquiryList.search);
    }, 2000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inquiryList.search]);

  useEffect(() => {
    if (
      JSON.stringify(prevInquiryList.current) !== JSON.stringify(inquiryList)
    ) {
      resetPagination();
      resetPaginationMobile();
      prevInquiryList.current = inquiryList;
    }
  }, [inquiryList]);

  useEffect(() => {
    if (pagination.current === PAGINATION.PAGE) {
      refetchInquiry();
    }
  }, [pagination.current]);

  useEffect(() => {
    if (paginationMobile.current === PAGINATION.PAGE) {
      refetchInquiryMobile();
    }
  }, [paginationMobile.current]);

  useEffect(() => {
    if (inquiryData?.data?.meta?.total_items) {
      updateTotal(inquiryData.data.meta.total_items);
    }
  }, [inquiryData]);

  useEffect(() => {
    if (inquiryMobileData?.data?.meta?.total_items) {
      updateTotalMobile(inquiryMobileData.data.meta.total_items);
    }
  }, [inquiryMobileData]);

  useEffect(() => {
    if (inquiryErr) void message.error(parseErrorMessage(inquiryErr));
  }, [inquiryErr]);

  useEffect(() => {
    if (inquiryMobileErr)
      void message.error(parseErrorMessage(inquiryMobileErr));
  }, [inquiryMobileErr]);

  useEffect(() => {
    setHeaderTitle("お問い合わせ一覧");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full product-list">
      <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-[10px] md:gap-[20px] self-stretch">
          <CustomBreadCrumb
            items={[
              {
                label: "ユーザー管理",
                link: "/admin/company-management",
              },
              {
                label: `ユーザー詳細（ID: #${companyCode}）`,
                link: `/admin/company-management/${companyCode}/view`,
              },
              {
                label: "お問い合わせ一覧",
                isCurrent: true,
              },
            ]}
          />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="件名などで検索"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          <div
            data-プロパティ1="base"
            className="inline-flex justify-start items-center cursor-pointer"
            onClick={toggleFilter}
          >
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              検索条件を追加
            </div>
            <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
          </div>
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    お問い合わせ種別
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="type"
                      defaultValue={plainTypeOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedTypeList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainTypeOptions
                            : [];
                          setCheckedTypeList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateType}
                              onChange={onCheckAllChange}
                              checked={checkAllType}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(INQUIRY_FILTER_USER_TYPE)}
                              value={checkedTypeList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    ステータス
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="status"
                      defaultValue={plainStatusOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedStatusList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainStatusOptions
                            : [];
                          setCheckedStatusList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateStatus}
                              onChange={onCheckAllChange}
                              checked={checkAllStatus}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(
                                INQUIRY_FILTER_STATUS_TYPE,
                              )}
                              value={checkedStatusList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group
                          {...field}
                          options={Object.values(INQUIRY_FILTER_SORT)}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
          <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-between items-center gap-3.5">
            <div className="hidden self-stretch md:inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!inquiryData?.data?.items &&
                  selectedRowKeys.length === inquiryData.data.items.length &&
                  inquiryData.data.items.length > 0
                }
                indeterminate={
                  !!inquiryData?.data?.items &&
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < inquiryData.data.items.length
                }
                onChange={(e) => handleCheckAll(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="md:hidden self-stretch inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!inquiryMobileData?.data?.items &&
                  selectedRowKeyMobile.length ===
                    inquiryMobileData.data.items.length &&
                  inquiryMobileData.data.items.length > 0
                }
                indeterminate={
                  !!inquiryMobileData?.data?.items &&
                  selectedRowKeyMobile.length > 0 &&
                  selectedRowKeyMobile.length <
                    inquiryMobileData.data.items.length
                }
                onChange={(e) => handleCheckAllMobile(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-3.5">
              <Popconfirm
                title="タスクを削除する"
                description="このタスクを削除してもよろしいですか?"
                onConfirm={confirm}
                okText="はい"
                cancelText="いいえ"
                disabled={
                  isEmpty(selectedRowKeys) && isEmpty(selectedRowKeyMobile)
                }
              >
                <div
                  data-プロパティ1="text only"
                  className="flex justify-start items-center cursor-pointer"
                >
                  <div className="justify-start text-blue-900 text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                    <>チェックした項目を削除</>
                  </div>
                </div>
              </Popconfirm>
              <Button
                type="primary"
                className="h-10 min-w-24 bg-primary"
                icon={
                  <IconDownload2 className="w-[20px] h-[20px] aspect-[1/1]" />
                }
                onClick={downloadCsv}
              >
                CSVデータをダウンロード
              </Button>
            </div>
          </div>
          <Table<Inquiry>
            className="w-full hidden md:block"
            rowKey="code"
            rowSelection={rowSelection}
            onChange={onChangePage}
            pagination={!isEmpty(inquiryData?.data?.items) ? pagination : false}
            columns={columns}
            dataSource={inquiryData?.data?.items || []}
          />
          <PaginateListMobile<Inquiry>
            selectedRowKeyMobile={selectedRowKeyMobile}
            setSelectedRowKeyMobile={setSelectedRowKeyMobile}
            pagination={
              !isEmpty(inquiryData?.data?.items) ? paginationMobile : false
            }
            dataSource={inquiryMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <InquiryItem<Inquiry>
                inquiry={item}
                checked={checked}
                onSelect={onSelect}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};

export default CompanyRequestsPage;
